import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import axios, { isAxiosError } from "axios";
import { SyncOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";

import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import Text from "@/common/Text/Text";
import ControlPanelActionRow from "@/features/ui/modals/components/ControlPanelActionRow/ControlPanelActionRow";
import {
  canTargetSingleZone,
  useCreateScreensaverMutation,
  useListScreensaversQuery,
  useSignScreensaverUploadMutation,
} from "@/features/screensavers/screensaversApi";
import {
  displayScreensaverName,
  formatCurrentScreensaverLabel,
  resolvePreviewMediaUrl,
} from "@/features/screensavers/screensaverPanelModel";
import {
  formatScreensaverEnabledLabel,
  resolveScreensaverIdlePolicy,
} from "@/features/screensavers/screensaverIdlePolicy";
import {
  useGetScreensaverPackageStatusQuery,
  useLazyGetAssistantPackageStatusQuery,
  useLazyGetPlayerPackageStatusQuery,
  useLazyGetScreensaverPackageStatusQuery,
  useRefreshSoftwareInventoryMutation,
} from "@/features/devices/devicesApi";
import {
  formatAvailableVersionLabel,
  formatInstalledVersionLabel,
  formatInventoryVerificationCaption,
  isSoftwareStatusUpToDate,
  isSoftwareUpdateEligible,
} from "@/features/devices/softwareInventoryModel";
import {
  resolveSoftwareInventoryFleetPollingIntervalMs,
  SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
} from "@/features/devices/softwareInventoryPollingPolicy";
import {
  SOFTWARE_INVENTORY_REFRESH_COPY,
  newestInventoryObservedAt,
  runManualSoftwareInventoryRefresh,
} from "@/features/devices/softwareInventoryRefresh";

import ScreensaverAssignmentSections from "@/features/screensavers/ScreensaverAssignmentSections";

import styles from "../ui/modals/components/DevicesControlPanel/DevicesControlPanel.module.scss";

const DISPLAY_HINT = "1280×800 · H.264 · MP4 · no audio (RK3568 Player)";

type PreviewMediaState = "loading" | "ready" | "error";

interface Props {
  ids: string[];
  commandsEnabled: boolean;
  isMutating: boolean;
  selectedScreensaverId: string | null;
  /**
   * Durable device-active contentId from Fleet device SoT
   * (package ack → device row). Not Library selection.
   */
  activeContentId: string | null;
  /** Optional Fleet release id linked to last successful Apply. */
  activeReleaseId?: string | null;
  /**
   * Device-confirmed package enabled state from Fleet device SoT
   * (`screensaver_enabled`). Do not infer from last button click.
   */
  screensaverEnabled?: boolean | null;
  /** Optional device/Fleet idle timeout (ms); defaults to canonical 60s policy. */
  idleTimeoutMs?: number | null;
  /** True while Screensaver APK OTA command is in flight. */
  isMutatingSoftware?: boolean;
  /**
   * When true, poll Fleet screensaver package GET so Installed converges
   * after OTA without browser refresh / manual Refresh.
   */
  softwareOtaSyncActive?: boolean;
  deviceAppearsOffline?: boolean;
  onSelectScreensaver: (id: string | null) => void;
  onApply: () => void;
  onEnable: () => void;
  onDisable: () => void;
  /** Owner-facing Screensaver APK OTA (update_kk_package). */
  onUpdateSoftware?: () => void;
  /** Remote Console modal currently open. Default false (fail closed). */
  consoleOpen?: boolean;
}

const ScreensaverPanel: React.FC<Props> = ({
  ids,
  commandsEnabled,
  isMutating,
  selectedScreensaverId,
  activeContentId,
  activeReleaseId = null,
  screensaverEnabled = null,
  idleTimeoutMs = null,
  isMutatingSoftware = false,
  softwareOtaSyncActive = false,
  deviceAppearsOffline = false,
  onSelectScreensaver,
  onApply,
  onEnable,
  onDisable,
  onUpdateSoftware,
  consoleOpen = false,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isRefreshPending, setIsRefreshPending] = useState(false);
  const refreshCancelledRef = useRef(false);
  const [documentHidden, setDocumentHidden] = useState(() =>
    typeof document !== "undefined"
      ? document.visibilityState === "hidden"
      : false
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [previewMediaState, setPreviewMediaState] =
    useState<PreviewMediaState>("loading");
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data, isFetching } = useListScreensaversQuery();
  const [signUpload] = useSignScreensaverUploadMutation();
  const [createScreensaver] = useCreateScreensaverMutation();
  const singleZone = canTargetSingleZone(ids);
  const packageDeviceId = singleZone ? ids[0] : null;
  const inventoryPollMs = resolveSoftwareInventoryFleetPollingIntervalMs({
    singleZoneSelected: singleZone,
    consoleOpen,
    documentHidden,
    otaSyncActive: softwareOtaSyncActive,
    deviceAppearsOffline,
  });
  const {
    data: packageStatus,
    isFetching: packageFetching,
    refetch: refetchPackageStatus,
  } = useGetScreensaverPackageStatusQuery(
    { deviceId: packageDeviceId || "" },
    {
      // Bulk selection / closed console → no per-device inventory subscription.
      skip: !packageDeviceId || !singleZone || !consoleOpen,
      pollingInterval: inventoryPollMs,
      ...SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS,
    }
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      setDocumentHidden(hidden);
      if (
        !hidden &&
        packageDeviceId &&
        singleZone &&
        softwareOtaSyncActive
      ) {
        void refetchPackageStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [
    packageDeviceId,
    singleZone,
    softwareOtaSyncActive,
    refetchPackageStatus,
  ]);
  const [refreshSoftwareInventory] = useRefreshSoftwareInventoryMutation();
  const [fetchPlayerPackage] = useLazyGetPlayerPackageStatusQuery();
  const [fetchAssistantPackage] = useLazyGetAssistantPackageStatusQuery();
  const [fetchScreensaverPackage] = useLazyGetScreensaverPackageStatusQuery();
  const isRefreshingInventory = isRefreshPending;

  useEffect(() => {
    refreshCancelledRef.current = false;
    return () => {
      refreshCancelledRef.current = true;
    };
  }, [packageDeviceId]);

  const screensavers = data?.screensavers || [];
  const selected = useMemo(
    () => screensavers.find((s) => s.id === selectedScreensaverId) || null,
    [screensavers, selectedScreensaverId]
  );
  const currentLabel = useMemo(
    () =>
      formatCurrentScreensaverLabel({
        activeContentId,
        releaseId: activeReleaseId,
        screensavers,
      }),
    [activeContentId, activeReleaseId, screensavers]
  );
  const zoneOk =
    commandsEnabled &&
    singleZone &&
    !isMutating &&
    !isMutatingSoftware &&
    !uploading;
  const previewEnabled = Boolean(selected && resolvePreviewMediaUrl(selected));
  const idlePolicy = resolveScreensaverIdlePolicy({ idleTimeoutMs });
  const enabledLabel = formatScreensaverEnabledLabel(screensaverEnabled);
  const stateKnown = screensaverEnabled === true || screensaverEnabled === false;
  const installedDisplay = formatInstalledVersionLabel({
    status: packageStatus,
    loading: packageFetching,
    singleZone,
  });
  const verifiedCaption = formatInventoryVerificationCaption({
    status: packageStatus,
    loading: packageFetching,
    singleZone,
  });
  const availableDisplay = formatAvailableVersionLabel(packageStatus);
  const upToDate = isSoftwareStatusUpToDate(packageStatus);
  const softwareUpdateOk =
    commandsEnabled &&
    singleZone &&
    !isMutating &&
    !isMutatingSoftware &&
    !isRefreshingInventory &&
    isSoftwareUpdateEligible({ status: packageStatus }) &&
    Boolean(onUpdateSoftware);

  const handleRefreshInstalledVersions = async () => {
    if (!commandsEnabled || !singleZone || !packageDeviceId) {
      toast.error("Select exactly one Zone to refresh installed versions");
      return;
    }
    if (isRefreshPending) return;

    const baselineObservedAt = newestInventoryObservedAt([packageStatus]);
    setIsRefreshPending(true);
    refreshCancelledRef.current = false;
    try {
      const outcome = await runManualSoftwareInventoryRefresh({
        deviceAppearsOffline,
        baselineObservedAt,
        publish: () =>
          refreshSoftwareInventory({ deviceId: packageDeviceId }).unwrap(),
        fetchStatuses: async () => {
          const [player, assistant, screensaver] = await Promise.all([
            fetchPlayerPackage({ deviceId: packageDeviceId }).unwrap(),
            fetchAssistantPackage({ deviceId: packageDeviceId }).unwrap(),
            fetchScreensaverPackage({ deviceId: packageDeviceId }).unwrap(),
          ]);
          await refetchPackageStatus();
          return [player, assistant, screensaver];
        },
        isCancelled: () => refreshCancelledRef.current,
      });
      if (outcome === "confirmed") {
        toast.success(SOFTWARE_INVENTORY_REFRESH_COPY.confirmed);
      } else if (outcome !== "cancelled") {
        toast.error(SOFTWARE_INVENTORY_REFRESH_COPY[outcome]);
      }
    } finally {
      setIsRefreshPending(false);
    }
  };

  // Close browser preview when the selected library row changes.
  useEffect(() => {
    setPreviewUrl(null);
    setPreviewTitle("");
    setPreviewMediaState("loading");
    setPreviewError(null);
  }, [selectedScreensaverId]);

  const handleUpload = async (file: File | null) => {
    if (!file || !commandsEnabled) return;
    if (!file.name.toLowerCase().endsWith(".mp4")) {
      toast.error("Screensaver must be an MP4 file");
      return;
    }
    setUploading(true);
    try {
      const signed = await signUpload({ filename: file.name }).unwrap();
      await axios.put(signed.url, file, {
        headers: { "Content-Type": "video/mp4" },
      });
      const created = await createScreensaver({
        filename: file.name,
        name: file.name.replace(/\.mp4$/i, ""),
        displayProfileId: "rk3568_player",
      }).unwrap();
      onSelectScreensaver(created.screensaver.id);
      toast.success("Screensaver uploaded (library only — not applied)");
    } catch (error) {
      const message =
        (isAxiosError(error) &&
          (error.response?.data as { message?: string } | undefined)?.message) ||
        (error as { data?: { message?: string } })?.data?.message ||
        "Upload failed";
      toast.error(String(message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handlePreview = () => {
    const url = resolvePreviewMediaUrl(selected);
    if (!url) {
      toast.error("Select a screensaver with a playable media URL to preview");
      return;
    }
    setPreviewTitle(selected ? displayScreensaverName(selected) : "Screensaver");
    setPreviewMediaState("loading");
    setPreviewError(null);
    setPreviewUrl(url);
    toast.success("Previewing in browser only — not sent to Player");
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewTitle("");
    setPreviewMediaState("loading");
    setPreviewError(null);
  };

  const libraryGuidance =
    screensavers.length === 0
      ? "Upload a Screensaver to populate Library, then select it to enable Preview."
      : !selected
        ? "Select a Library asset, then Preview to inspect in Super Admin (does not Apply or change Current)."
        : "Preview inspects the selected Library candidate locally. It does not Apply or change Current.";

  return (
    <div data-testid="screensaver-panel">
      <div className="mb-3" data-testid="screensaver-software-section">
        <Text type="title" size="md">
          Screen Saver Software
        </Text>
        <Text type="text" size="sm" className="d-block mb-2">
          Manages the Screensaver APK package. Separate from Library content
          Apply / Device Control.
        </Text>

        <ControlPanelActionRow
          className={styles.firstActionRow}
          leftComponent={<Text size="md">Installed Version</Text>}
          rightComponent={
            <div data-testid="screensaver-software-installed">
              <Text size="md">
                {installedDisplay}
                {packageFetching && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </Text>
              {verifiedCaption && (
                <span
                  className={styles.inventoryVerifiedCaption}
                  title={verifiedCaption.exactAt || undefined}
                  data-testid="screensaver-software-verified"
                >
                  {verifiedCaption.text}
                </span>
              )}
            </div>
          }
        />

        <ControlPanelActionRow
          leftComponent={<Text size="md">Available Version</Text>}
          rightComponent={
            <div data-testid="screensaver-software-available">
              <Text size="md">{availableDisplay}</Text>
            </div>
          }
        />

        <ControlPanelActionRow
          leftComponent={<Text size="md">Refresh Installed Versions</Text>}
          rightComponent={
            <div className="d-grid justify-content-end">
              <ButtonSmall
                type="button"
                disabled={
                  !commandsEnabled ||
                  !singleZone ||
                  isRefreshingInventory ||
                  isMutating ||
                  isMutatingSoftware
                }
                onClick={handleRefreshInstalledVersions}
                data-testid="screensaver-software-refresh"
              >
                <Text size="md">
                  {isRefreshingInventory && (
                    <SyncOutlined
                      className={styles.refreshButtonSpinner}
                      spin
                    />
                  )}
                  {isRefreshingInventory ? "Refreshing…" : "Refresh"}
                </Text>
              </ButtonSmall>
            </div>
          }
        />

        {upToDate ? (
          <div
            className="alert alert-secondary py-2 mb-2"
            role="status"
            data-testid="screensaver-software-up-to-date"
          >
            Software is up to date.
          </div>
        ) : (
          <ControlPanelActionRow
            borderBottom={false}
            leftComponent={
              <div className="d-flex align-items-center">
                {isMutatingSoftware && (
                  <SyncOutlined className={styles.syncLoader} spin />
                )}
              </div>
            }
            rightComponent={
              <div className="d-grid justify-content-end">
                <ButtonSmall
                  type="button"
                  disabled={!softwareUpdateOk}
                  onClick={onUpdateSoftware}
                  data-testid="screensaver-software-update"
                >
                  <Text size="md">Update Screensaver</Text>
                </ButtonSmall>
              </div>
            }
          />
        )}
      </div>

      <ScreensaverAssignmentSections commandsEnabled={commandsEnabled} />

      <Text type="title" size="md">
        Screensaver
      </Text>
      <Text type="text" size="sm">
        {DISPLAY_HINT}. Apply targets exactly one Zone.
      </Text>
      <Text className={styles.zoneText} size="sm">
        Zone(s) Selected: {ids.length}
      </Text>
      {!singleZone && (
        <div
          className="alert alert-warning py-2 mb-2"
          role="status"
          data-testid="screensaver-single-zone-required"
        >
          Select exactly one Zone to Apply / Enable / Disable / Update Software.
        </div>
      )}

      <ControlPanelActionRow
        className={styles.firstActionRow}
        leftComponent={<Text size="md">Current Screensaver</Text>}
        rightComponent={
          <div data-testid="screensaver-current-name">
            <Text size="md">
              {currentLabel}
              {isFetching && <SyncOutlined className={styles.syncLoader} spin />}
            </Text>
          </div>
        }
      />

      <ControlPanelActionRow
        leftComponent={<Text size="md">Library</Text>}
        rightComponent={
          <select
            className="form-select form-select-sm"
            data-testid="screensaver-library-select"
            value={selectedScreensaverId || ""}
            onChange={(e) => onSelectScreensaver(e.target.value || null)}
            disabled={!commandsEnabled || uploading}
          >
            <option value="">None</option>
            {screensavers.map((s) => (
              <option key={s.id} value={s.id}>
                {displayScreensaverName(s)}
              </option>
            ))}
          </select>
        }
      />

      <div
        className="alert alert-secondary py-2 mb-2"
        role="status"
        data-testid="screensaver-library-guidance"
      >
        {libraryGuidance}
      </div>

      <ControlPanelActionRow
        leftComponent={
          <div className="d-flex align-items-center">
            <Text size="md">Upload Screensaver</Text>
            {uploading && <SyncOutlined className={styles.syncLoader} spin />}
          </div>
        }
        rightComponent={
          <div className="d-grid justify-content-end">
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,.mp4"
              data-testid="screensaver-file-input"
              style={{ display: "none" }}
              onChange={(e) => handleUpload(e.target.files?.[0] || null)}
            />
            <ButtonSmall
              type="button"
              disabled={!commandsEnabled || uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="screensaver-upload"
            >
              <Text size="md">Upload Screensaver</Text>
            </ButtonSmall>
          </div>
        }
      />

      <ControlPanelActionRow
        leftComponent={<Text size="md">Preview</Text>}
        rightComponent={
          <div className="d-grid justify-content-end">
            <ButtonSmall
              type="button"
              disabled={!previewEnabled}
              onClick={handlePreview}
              data-testid="screensaver-preview"
              title={
                previewEnabled
                  ? "Preview selected Library MP4 in Super Admin only"
                  : "Select a Library screensaver to enable Preview"
              }
            >
              <Text size="md">Preview</Text>
            </ButtonSmall>
          </div>
        }
      />

      <ControlPanelActionRow
        leftComponent={
          <div className="d-flex align-items-center">
            <Text size="md">Apply to Zone</Text>
            {isMutating && <SyncOutlined className={styles.syncLoader} spin />}
          </div>
        }
        rightComponent={
          <div className="d-grid justify-content-end">
            <ButtonSmall
              type="button"
              disabled={!zoneOk || !selectedScreensaverId}
              onClick={onApply}
              data-testid="screensaver-apply"
            >
              <Text size="md">Apply to Zone</Text>
            </ButtonSmall>
          </div>
        }
      />

      <div className="mt-2 mb-1" data-testid="screensaver-device-control">
        <Text type="title" size="md">
          Device Control
        </Text>
      </div>

      <ControlPanelActionRow
        leftComponent={<Text size="md">Screensaver</Text>}
        rightComponent={
          <div data-testid="screensaver-enabled-state">
            <Text size="md">{enabledLabel}</Text>
          </div>
        }
      />

      <ControlPanelActionRow
        leftComponent={<Text size="md">Screen Timeout</Text>}
        rightComponent={
          <div data-testid="screensaver-screen-timeout">
            <Text size="md">{idlePolicy.screenTimeoutLabel}</Text>
          </div>
        }
      />

      {screensaverEnabled === false && (
        <ControlPanelActionRow
          leftComponent={<Text size="md">Idle Display</Text>}
          rightComponent={
            <div data-testid="screensaver-idle-display">
              <Text size="md">{idlePolicy.disabledIdleDisplayLabel}</Text>
            </div>
          }
        />
      )}

      <ControlPanelActionRow
        borderBottom={false}
        leftComponent={
          <div className="d-flex align-items-center">
            {isMutating && <SyncOutlined className={styles.syncLoader} spin />}
          </div>
        }
        rightComponent={
          <div className="d-grid justify-content-end">
            {screensaverEnabled === true && (
              <ButtonSmall
                type="button"
                disabled={!zoneOk}
                onClick={onDisable}
                data-testid="screensaver-disable"
              >
                <Text size="md">Disable Screensaver</Text>
              </ButtonSmall>
            )}
            {screensaverEnabled === false && (
              <ButtonSmall
                type="button"
                disabled={!zoneOk}
                onClick={onEnable}
                data-testid="screensaver-enable"
              >
                <Text size="md">Enable Screensaver</Text>
              </ButtonSmall>
            )}
            {!stateKnown && (
              <div
                className="d-flex gap-2 justify-content-end"
                data-testid="screensaver-state-unknown-actions"
              >
                <ButtonSmall
                  type="button"
                  disabled={!zoneOk}
                  onClick={onEnable}
                  data-testid="screensaver-enable"
                >
                  <Text size="md">Enable Screensaver</Text>
                </ButtonSmall>
                <ButtonSmall
                  type="button"
                  disabled={!zoneOk}
                  onClick={onDisable}
                  data-testid="screensaver-disable"
                >
                  <Text size="md">Disable Screensaver</Text>
                </ButtonSmall>
              </div>
            )}
          </div>
        }
      />

      {previewUrl &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            data-testid="screensaver-preview-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={`Preview — ${previewTitle || "Screensaver"}`}
            onClick={closePreview}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10050,
              background: "rgba(0,0,0,0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div
              data-testid="screensaver-preview-player"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(800px, 100%)",
                background: "#111",
                borderRadius: 8,
                padding: 16,
                boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                  gap: 12,
                }}
              >
                <Text type="title" size="md">
                  Preview — {previewTitle || "Screensaver"}
                </Text>
                <ButtonSmall
                  type="button"
                  onClick={closePreview}
                  data-testid="screensaver-preview-close"
                >
                  <Text size="md">Close</Text>
                </ButtonSmall>
              </div>
              <Text type="text" size="sm" className="mb-2 d-block">
                Browser-only preview. This does not send a Fleet command or
                change Current Screensaver.
              </Text>
              <div
                data-testid="screensaver-preview-frame"
                style={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "8 / 5",
                  background: "#000",
                  overflow: "hidden",
                }}
              >
                {previewMediaState === "loading" && (
                  <div
                    data-testid="screensaver-preview-loading"
                    role="status"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ddd",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  >
                    Loading preview…
                  </div>
                )}
                {previewMediaState === "error" && (
                  <div
                    data-testid="screensaver-preview-error"
                    role="alert"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 16,
                      textAlign: "center",
                      color: "#f5c2c7",
                      background: "rgba(0,0,0,0.85)",
                      zIndex: 2,
                    }}
                  >
                    {previewError ||
                      "Could not load this Screensaver MP4 for preview."}
                  </div>
                )}
                <video
                  key={previewUrl}
                  data-testid="screensaver-preview-video"
                  src={previewUrl}
                  controls
                  autoPlay
                  muted
                  playsInline
                  loop
                  onLoadStart={() => {
                    setPreviewMediaState("loading");
                    setPreviewError(null);
                  }}
                  onCanPlay={() => {
                    setPreviewMediaState("ready");
                    setPreviewError(null);
                  }}
                  onError={() => {
                    setPreviewMediaState("error");
                    setPreviewError(
                      "Could not load this Screensaver MP4 for preview. Check the Library asset URL and try again."
                    );
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    background: "#000",
                    display: "block",
                  }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ScreensaverPanel;
